import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';

import type { RootStackParamList } from '../navigation/stackTypes';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { isSuccessful } from '../services/api-requests';
import {
  cancelRfidPairing,
  listRfidCards,
  startRfidPairing,
} from '../services/rfid-service';
import { notificationService } from '../services/notification-service';
import { ApiRfidCard } from '../models/rfid-card-model';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'AddRfidTag'>;

/**
 * AddRfidTag — implements §4.2 "Add a tag" of the contract.
 *
 *  1. POST /rfid/pairing/start → opens a 60s pairing window keyed by
 *     `claim_token` + `card_uuid`.
 *  2. User taps a physical card on a Meander charger; the backend rotates the
 *     PENDING_CLAIM row into ACTIVE and (optionally) fires the
 *     MATCHED_PENDING_CLAIM push.
 *  3. We poll `GET /rfid/cards` every 3s as a fallback (per §3.2.2) so the
 *     pairing flow works even without push permission. Whichever signal lands
 *     first (poll or push) advances the UI.
 *  4. On user cancel before tap → POST /rfid/pairing/cancel so the backend
 *     marks the row REVOKED rather than letting it expire harmlessly.
 */

const POLL_INTERVAL_MS = 3000;
const WINDOW_SECONDS = 60;

const AddRfidTag: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [nickname, setNickname] = useState<string>('');
  const [phase, setPhase] = useState<'idle' | 'waiting' | 'success' | 'expired'>(
    'idle',
  );
  const [pendingCardUuid, setPendingCardUuid] = useState<string | null>(null);
  const [claimToken, setClaimToken] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(WINDOW_SECONDS);
  const [starting, setStarting] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiresAtRef = useRef<number | null>(null);

  const pulseAnim = useRef(new Animated.Value(1)).current;

  // Heartbeat animation for the contactless icon while we wait for the tap.
  useEffect(() => {
    if (phase !== 'waiting') {
      pulseAnim.setValue(1);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.15,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [phase, pulseAnim]);

  const cleanupTimers = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanupTimers(), [cleanupTimers]);

  // Cancel an in-flight pairing on unmount so the row doesn't dangle.
  useEffect(
    () => () => {
      if (claimToken && phase === 'waiting') {
        cancelRfidPairing(claimToken).catch(() => {});
      }
    },
    [claimToken, phase],
  );

  const handlePaired = useCallback(
    (card: ApiRfidCard) => {
      cleanupTimers();
      setPhase('success');
      notificationService.sendNotification(
        'success',
        'Tag paired',
        `${card.nickname || `Card ••${card.last4_uid ?? ''}`} is ready to use.`,
      );
      // Brief celebration, then jump into the tag's detail page so the user
      // can rename / configure it.
      setTimeout(() => {
        navigation.replace('RfidCardSettings', { cardUuid: card.uuid });
      }, 900);
    },
    [cleanupTimers, navigation],
  );

  const startPolling = useCallback(
    (cardUuid: string) => {
      if (pollRef.current) return;
      pollRef.current = setInterval(async () => {
        try {
          const resp = await listRfidCards();
          if (!isSuccessful(resp.status) || !resp.payload) return;
          const match = resp.payload.cards.find((c) => c.uuid === cardUuid);
          // The card transitions from PENDING_CLAIM → ACTIVE on first tap;
          // that's our success signal.
          if (match && match.status === 'ACTIVE') {
            handlePaired(match);
          }
        } catch {
          // Swallow transient errors; next tick retries.
        }
      }, POLL_INTERVAL_MS);
    },
    [handlePaired],
  );

  const startCountdown = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      const expiresAt = expiresAtRef.current;
      if (!expiresAt) return;
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        cleanupTimers();
        setPhase('expired');
      }
    }, 250);
  }, [cleanupTimers]);

  const onStart = useCallback(async () => {
    setStarting(true);
    try {
      const trimmedNickname = nickname.trim();
      const resp = await startRfidPairing(
        trimmedNickname ? { nickname: trimmedNickname } : undefined,
      );
      if (!isSuccessful(resp.status) || !resp.payload) {
        notificationService.sendNotification(
          'error',
          'Could not start pairing. Please try again.',
        );
        return;
      }
      const { card_uuid, claim_token, expires_at } = resp.payload;
      setPendingCardUuid(card_uuid);
      setClaimToken(claim_token);
      const expiresAtMs = new Date(expires_at).getTime();
      expiresAtRef.current = Number.isFinite(expiresAtMs)
        ? expiresAtMs
        : Date.now() + WINDOW_SECONDS * 1000;
      setSecondsLeft(WINDOW_SECONDS);
      setPhase('waiting');
      startPolling(card_uuid);
      startCountdown();
    } catch (e) {
      console.error('[AddRfidTag] start error:', e);
      notificationService.sendNotification(
        'error',
        'Could not start pairing. Please try again.',
      );
    } finally {
      setStarting(false);
    }
  }, [nickname, startPolling, startCountdown]);

  const onCancel = useCallback(async () => {
    if (!claimToken) {
      navigation.goBack();
      return;
    }
    setCancelling(true);
    try {
      await cancelRfidPairing(claimToken);
    } catch {
      // Even on failure, leave the pairing UI — the row will expire on its own.
    } finally {
      cleanupTimers();
      setCancelling(false);
      navigation.goBack();
    }
  }, [claimToken, cleanupTimers, navigation]);

  const onRetry = useCallback(() => {
    cleanupTimers();
    setPhase('idle');
    setPendingCardUuid(null);
    setClaimToken(null);
    expiresAtRef.current = null;
    setSecondsLeft(WINDOW_SECONDS);
  }, [cleanupTimers]);

  const timerText = useMemo(() => {
    const mm = Math.floor(secondsLeft / 60);
    const ss = secondsLeft % 60;
    return `${mm}:${String(ss).padStart(2, '0')}`;
  }, [secondsLeft]);

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {Platform.OS !== 'web' && <Header headerText="Add an RFID Tag" />}

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.iconWrap}>
          <Animated.View
            style={[
              styles.iconCircle,
              phase === 'waiting' && { transform: [{ scale: pulseAnim }] },
              phase === 'success' && styles.iconCircleSuccess,
              phase === 'expired' && styles.iconCircleExpired,
            ]}
          >
            {phase === 'success' ? (
              <MaterialIcons name="check" size={48} color="#FFFFFF" />
            ) : phase === 'expired' ? (
              <MaterialIcons name="timer-off" size={42} color="#FFFFFF" />
            ) : (
              <MaterialCommunityIcons
                name="contactless-payment"
                size={48}
                color="#FFFFFF"
              />
            )}
          </Animated.View>
        </View>

        {phase === 'idle' && (
          <>
            <Text style={styles.title}>Pair a new tag</Text>
            <Text style={styles.body}>
              Tap your card on any Meander charger within 60 seconds. The
              first tap pairs the card to your account and is the only tap
              that requires the app.
            </Text>

            <Text style={styles.fieldLabel}>Nickname (optional)</Text>
            <TextInput
              style={styles.input}
              value={nickname}
              onChangeText={setNickname}
              placeholder="e.g. Work keychain"
              placeholderTextColor="#6C6F75"
              autoCapitalize="words"
              maxLength={40}
            />

            <LoadingButton
              onPress={onStart}
              isLoading={starting}
              backgroundColor="#40BFB4"
              style={styles.primaryButton}
            >
              Start pairing
            </LoadingButton>
          </>
        )}

        {phase === 'waiting' && (
          <>
            <Text style={styles.title}>Tap your card now</Text>
            <Text style={styles.body}>
              Hold the card flat against any Meander charger reader. We&apos;ll
              update this screen as soon as the tap lands.
            </Text>

            <View style={styles.timerWrap}>
              <Text style={styles.timerText}>{timerText}</Text>
              <Text style={styles.timerLabel}>remaining</Text>
            </View>

            <View style={styles.tipRow}>
              <ActivityIndicator size="small" color="#40BFB4" />
              <Text style={styles.tipText}>Waiting for tap…</Text>
            </View>

            <LoadingButton
              onPress={onCancel}
              isLoading={cancelling}
              backgroundColor="#3A3D42"
              style={styles.primaryButton}
            >
              Cancel
            </LoadingButton>
          </>
        )}

        {phase === 'expired' && (
          <>
            <Text style={styles.title}>Pairing window closed</Text>
            <Text style={styles.body}>
              We didn&apos;t see a tap within 60 seconds. Start the flow again
              and tap your card on a Meander charger as soon as the next
              window opens.
            </Text>
            <LoadingButton
              onPress={onRetry}
              backgroundColor="#40BFB4"
              style={styles.primaryButton}
            >
              Try again
            </LoadingButton>
            <Pressable
              onPress={() => navigation.goBack()}
              style={({ pressed }) => [
                styles.linkBtn,
                { opacity: pressed ? 0.6 : 1 },
              ]}
            >
              <Text style={styles.linkText}>Back to tags</Text>
            </Pressable>
          </>
        )}

        {phase === 'success' && (
          <>
            <Text style={styles.title}>Tag paired!</Text>
            <Text style={styles.body}>
              Your tag is now active. Hang tight — we&apos;re opening it up so
              you can name it and review settings.
            </Text>
          </>
        )}

        {pendingCardUuid && (phase === 'waiting' || phase === 'expired') && (
          <Text style={styles.footnote}>
            Card reference: {pendingCardUuid.slice(0, 8)}…
          </Text>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#1A1D21' },
  scrollContent: {
    paddingHorizontal: 24,
    paddingBottom: 60,
    alignItems: 'center',
  },
  iconWrap: { paddingVertical: 28 },
  iconCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#40BFB4',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#40BFB4',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 24,
  },
  iconCircleSuccess: { backgroundColor: '#10B981' },
  iconCircleExpired: { backgroundColor: '#8D8E90' },
  title: {
    color: '#FFFFFF',
    fontSize: 22,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
  },
  body: {
    color: '#B0B3B8',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 20,
    maxWidth: 360,
  },
  fieldLabel: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    alignSelf: 'stretch',
    marginBottom: 6,
  },
  input: {
    alignSelf: 'stretch',
    backgroundColor: '#292D33',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    borderWidth: 1,
    borderColor: '#3A3D42',
    marginBottom: 20,
  },
  primaryButton: {
    alignSelf: 'stretch',
    borderRadius: 12,
    paddingVertical: 4,
    marginTop: 8,
  },
  timerWrap: { alignItems: 'center', marginBottom: 12 },
  timerText: {
    color: '#FFFFFF',
    fontSize: 48,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '300',
    letterSpacing: 2,
  },
  timerLabel: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  tipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 28,
  },
  tipText: {
    color: '#B0B3B8',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  linkBtn: { marginTop: 12 },
  linkText: {
    color: '#40BFB4',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
  },
  footnote: {
    color: '#4A4D52',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 24,
  },
});

export default AddRfidTag;
